import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { queryKeys } from 'core/api/queryKeys';
import { ApiError } from 'core/errors/ApiError';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { assessmentApi } from 'features/assessment-dashboard/assessment.api';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { CanonicalKey, OrderedPatternTrail } from 'features/top-three-profiles/OrderedPatternTrail';
import { ProfileConnectionsEditor } from 'features/top-three-profiles/ProfileConnectionsEditor';
import { ProfileCoverageCard } from 'features/top-three-profiles/ProfileCoverageCard';
import { TopThreeProfileWorkbookCard } from 'features/workbooks/TopThreeProfileWorkbookCard';
import {
  EXPECTED_TOP_THREE_PROFILE_COUNT,
  MISSING_CONTENT_FILTERS,
  canonicalKey,
  completenessLabelKey,
  hasExactlySixScorablePatterns,
  hasPersonalSummary,
  isBuiltVersion,
  isProfileComplete,
  isPublishedProfile,
  isValidVersionId,
  logTopThreeDiagnostics,
  missingContentKeys,
  normalizeCoverage,
  normalizeRepairResult,
  readOrderedPatterns,
  relationCounts,
  resolveVersionPrimaryKey,
  selectBuiltVersion,
  versionPatterns,
} from 'features/top-three-profiles/topThreeProfiles';
import { Button } from 'shared/components/Button';
import { Badge, EmptyState, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { ListTable } from 'shared/components/ListTable';
import { Pagination } from 'shared/components/Pagination';
import { detailsAction, RowActionsCell, RowActionsHeader } from 'shared/components/RowActionsMenu';
import { SelectField, TextField } from 'shared/forms/Fields';
import { useToast } from 'shared/components/Toast';
import { formatDateTime, localizedName } from 'shared/utilities/format';

function buildProfileQuery(filters) {
  const params = {
    page: filters.page,
    per_page: filters.per_page,
    version_id: filters.version_id,
    canonical_key: filters.canonical_key,
    completeness: filters.completeness,
    missing: filters.missing,
  };
  if (filters.first_pattern_id) {
    params.first_pattern_id = filters.first_pattern_id;
  }
  if (filters.second_pattern_id) {
    params.second_pattern_id = filters.second_pattern_id;
  }
  if (filters.third_pattern_id) {
    params.third_pattern_id = filters.third_pattern_id;
  }
  return params;
}

function patternOptions(patterns, locale, t) {
  return [{ value: '', label: t('filters.all') }, ...(patterns || []).map((item) => ({
    value: String(item.id),
    label: localizedName(item, locale),
  }))];
}

export function TopThreeProfilesPage() {
  const { t, locale, dir } = useI18n();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedVersionId = searchParams.get('version_id') || '';
  const canManage = hasPermission(PERMISSIONS.topThreeProfilesManage);
  const repairInflightRef = useRef(false);
  const [repairStatus, setRepairStatus] = useState('');
  const [filters, setFilters] = useState({
    page: 1,
    per_page: 20,
    first_pattern_id: '',
    second_pattern_id: '',
    third_pattern_id: '',
    completeness: '',
    missing: '',
    canonical_key: '',
  });

  const versionsQuery = useQuery({ queryKey: queryKeys.versions, queryFn: ({ signal }) => assessmentApi.listVersions({ signal }) });
  const versions = useMemo(() => versionsQuery.data?.items || [], [versionsQuery.data]);
  const selectedVersion = useMemo(
    () => (versionsQuery.isSuccess ? selectBuiltVersion(versions, requestedVersionId) : null),
    [requestedVersionId, versions, versionsQuery.isSuccess],
  );
  const versionId = resolveVersionPrimaryKey(selectedVersion);
  const versionReady = versionsQuery.isSuccess && isValidVersionId(versionId);
  const queryFilters = useMemo(
    () => buildProfileQuery({ ...filters, version_id: versionId }),
    [filters, versionId],
  );
  const versionDetailQuery = useQuery({
    queryKey: queryKeys.version(versionId),
    enabled: versionReady,
    queryFn: ({ signal }) => assessmentApi.showVersion(versionId, { signal }),
  });
  const coverageQuery = useQuery({
    queryKey: queryKeys.topThreeProfileCoverage(versionId),
    enabled: versionReady,
    queryFn: ({ signal }) => assessmentApi.getTopThreeProfileCoverage(versionId, { signal }),
  });
  const profilesQuery = useQuery({
    queryKey: queryKeys.topThreeProfiles(queryFilters),
    enabled: versionReady,
    queryFn: ({ signal }) => assessmentApi.listTopThreeProfiles(versionId, queryFilters, { signal }),
    placeholderData: keepPreviousData,
  });

  const items = profilesQuery.data?.items || profilesQuery.data?.data || [];
  const meta = profilesQuery.data?.meta || {};
  const versionForActions = versionDetailQuery.data || selectedVersion;
  const versionPatternsForFilters = versionPatterns(versionForActions);
  const coverage = coverageQuery.isSuccess ? normalizeCoverage(coverageQuery.data || {}) : null;
  const foundProfiles = coverage?.foundProfiles;
  const identitiesComplete = foundProfiles === EXPECTED_TOP_THREE_PROFILE_COUNT;
  const canRepair = Boolean(
    canManage
    && versionReady
    && hasExactlySixScorablePatterns(versionForActions)
    && coverageQuery.isSuccess
    && Number(foundProfiles) < EXPECTED_TOP_THREE_PROFILE_COUNT,
  );

  const repair = useMutation({
    mutationFn: () => assessmentApi.repairTopThreeProfiles(versionId),
    onMutate: () => {
      setRepairStatus(t('profiles.generateBusy'));
    },
    onSuccess: async (data) => {
      const result = normalizeRepairResult(data);
      setRepairStatus(t('profiles.generateSuccess', result));
      toast.success(t('profiles.generateSuccess', result));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['assessment', 'top-three-profiles'] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.topThreeProfileCoverage(versionId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.versions }),
        queryClient.invalidateQueries({ queryKey: queryKeys.version(versionId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.overview }),
      ]);
    },
    onError: (error) => {
      logTopThreeDiagnostics({
        method: 'POST',
        url: `/admin/professional-assessment/versions/${versionId}/top-three-profiles/repair`,
        versionId,
        status: error?.status,
        code: error?.code,
      });
      if (error instanceof ApiError && error.isForbidden()) {
        navigate(paths.forbidden);
        return;
      }
      const message = humanizeApiError(error, t);
      setRepairStatus(message);
      toast.error(message);
    },
    onSettled: () => {
      repairInflightRef.current = false;
    },
  });

  const onVersionChange = (value) => {
    const nextVersion = selectBuiltVersion(versions, value);
    const nextId = resolveVersionPrimaryKey(nextVersion);
    setFilters((current) => ({
      ...current,
      first_pattern_id: '',
      second_pattern_id: '',
      third_pattern_id: '',
      page: 1,
    }));
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (nextId) {
        next.set('version_id', nextId);
      } else {
        next.delete('version_id');
      }
      return next;
    }, { replace: true });
  };

  useEffect(() => {
    if (!versionsQuery.isSuccess || !selectedVersion) {
      return;
    }
    const primaryKey = resolveVersionPrimaryKey(selectedVersion);
    if (!primaryKey || String(searchParams.get('version_id') || '') === primaryKey) {
      return;
    }
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('version_id', primaryKey);
      return next;
    }, { replace: true });
  }, [searchParams, selectedVersion, setSearchParams, versionsQuery.isSuccess]);

  useEffect(() => {
    const error = profilesQuery.error || coverageQuery.error;
    if (error instanceof ApiError && error.isForbidden()) {
      navigate(paths.forbidden);
    }
    if (profilesQuery.error instanceof ApiError) {
      logTopThreeDiagnostics({
        method: 'GET',
        url: `/admin/professional-assessment/versions/${versionId}/top-three-profiles`,
        versionId,
        status: profilesQuery.error.status,
        code: profilesQuery.error.code,
      });
    }
    if (coverageQuery.error instanceof ApiError) {
      logTopThreeDiagnostics({
        method: 'GET',
        url: `/admin/professional-assessment/versions/${versionId}/top-three-profiles/coverage`,
        versionId,
        status: coverageQuery.error.status,
        code: coverageQuery.error.code,
      });
    }
  }, [coverageQuery.error, navigate, profilesQuery.error, versionId]);

  const runRepair = () => {
    if (!canRepair || repair.isPending || repairInflightRef.current) {
      return;
    }
    repairInflightRef.current = true;
    repair.mutate();
  };

  const repairCounts = repair.data ? normalizeRepairResult(repair.data) : null;

  return (
    <div className="page-stack">
      <PageHeader
        title={t('nav.topThreeProfiles')}
        description={t('profiles.description')}
      />
      <div className="sr-only" aria-live="polite">{repairStatus}</div>
      <div className="toolbar wrap">
        <SelectField
          id="profile-version"
          data-testid="top-three-profile-version"
          label={t('results.version')}
          value={versionId}
          onChange={onVersionChange}
          options={versions.filter(isBuiltVersion).map((item) => ({
            value: String(item.id),
            label: `#${item.number}${item.status === 'draft' ? ` (${t('versions.statuses.draft')})` : ''}`,
          }))}
        />
      </div>
      <TopThreeProfileWorkbookCard
        versionId={versionId}
        version={versionForActions}
        coverageLoaded={coverageQuery.isSuccess}
        foundProfiles={foundProfiles}
        leadingActions={(
          <Button
            data-testid="generate-top-three-orderings"
            loading={repair.isPending}
            disabled={!canRepair || repair.isPending}
            onClick={runRepair}
          >
            {t('profiles.generateOrderings')}
          </Button>
        )}
        statusMessages={(
          <>
            {!versionReady ? (
              <p className="muted" role="status">{t('workbook.versionRequired')}</p>
            ) : null}
            {versionReady && !hasExactlySixScorablePatterns(versionForActions) ? (
              <p className="warning-banner" role="status" data-testid="generate-needs-six-patterns">
                {t('profiles.generateNeedsSixPatterns')}
              </p>
            ) : null}
            {versionReady && identitiesComplete ? (
              <p className="muted" role="status" data-testid="all-orderings-ready">{t('profiles.allOrderingsReady')}</p>
            ) : null}
            {repairCounts ? (
              <p className="muted" role="status" data-testid="repair-result-summary">
                {t('profiles.generateCounts', repairCounts)}
              </p>
            ) : null}
          </>
        )}
      />
      {coverageQuery.isError ? (
        <ErrorState
          title={t('profiles.coverageLoadFailed')}
          onRetry={coverageQuery.refetch}
          retryLabel={t('actions.retry')}
        />
      ) : null}
      {coverageQuery.isSuccess ? <ProfileCoverageCard coverage={coverageQuery.data} t={t} /> : null}
      <div className="toolbar wrap">
        <SelectField
          id="first-pattern"
          label={t('profiles.firstPattern')}
          value={filters.first_pattern_id}
          onChange={(value) => setFilters((current) => ({ ...current, first_pattern_id: value, page: 1 }))}
          options={patternOptions(versionPatternsForFilters, locale, t)}
          disabled={!versionReady}
        />
        <SelectField
          id="second-pattern"
          label={t('profiles.secondPattern')}
          value={filters.second_pattern_id}
          onChange={(value) => setFilters((current) => ({ ...current, second_pattern_id: value, page: 1 }))}
          options={patternOptions(versionPatternsForFilters, locale, t)}
          disabled={!versionReady}
        />
        <SelectField
          id="third-pattern"
          label={t('profiles.thirdPattern')}
          value={filters.third_pattern_id}
          onChange={(value) => setFilters((current) => ({ ...current, third_pattern_id: value, page: 1 }))}
          options={patternOptions(versionPatternsForFilters, locale, t)}
          disabled={!versionReady}
        />
        <SelectField
          id="completeness"
          label={t('profiles.completeness')}
          value={filters.completeness}
          onChange={(value) => setFilters((current) => ({ ...current, completeness: value, page: 1 }))}
          options={[
            { value: '', label: t('filters.all') },
            { value: 'complete', label: t('profiles.complete') },
            { value: 'incomplete', label: t('profiles.incomplete') },
          ]}
        />
        <SelectField
          id="missing-content"
          label={t('profiles.missingContentType')}
          value={filters.missing}
          onChange={(value) => setFilters((current) => ({ ...current, missing: value, page: 1 }))}
          options={[{ value: '', label: t('filters.all') }, ...MISSING_CONTENT_FILTERS.map((item) => ({
            value: item.value,
            label: t(item.labelKey),
          }))]}
        />
        <TextField
          id="canonical-key-search"
          label={t('profiles.canonicalKey')}
          value={filters.canonical_key}
          onChange={(value) => setFilters((current) => ({ ...current, canonical_key: value, page: 1 }))}
        />
      </div>
      {versionsQuery.isSuccess && !selectedVersion ? (
        <EmptyState title={t('profiles.noBuiltVersion')} />
      ) : null}
      {profilesQuery.isLoading ? <Skeleton rows={6} /> : null}
      {profilesQuery.isError ? (
        <ErrorState
          title={humanizeApiError(profilesQuery.error, t) || t('profiles.listLoadFailed')}
          onRetry={profilesQuery.refetch}
          retryLabel={t('actions.retry')}
        />
      ) : null}
      {versionReady && !profilesQuery.isLoading && !profilesQuery.isError && items.length === 0 ? <EmptyState title={t('empty.profiles')} /> : null}
      {items.length > 0 ? (
        <ListTable caption={t('nav.topThreeProfiles')} stickyActions>
          <thead>
            <tr>
              <th>{t('profiles.orderedPatterns')}</th>
              <th>{t('profiles.firstPattern')}</th>
              <th>{t('profiles.secondPattern')}</th>
              <th>{t('profiles.thirdPattern')}</th>
              <th>{t('profiles.canonicalKey')}</th>
              <th>{t('profiles.personalSummary')}</th>
              <th>{t('profiles.strengthCount')}</th>
              <th>{t('profiles.growthCount')}</th>
              <th>{t('profiles.careerPathCount')}</th>
              <th>{t('profiles.skillCount')}</th>
              <th>{t('profiles.completeness')}</th>
              <th>{t('answers.lastUpdated')}</th>
              <RowActionsHeader />
            </tr>
          </thead>
          <tbody>
            {items.map((profile) => {
              const ordered = readOrderedPatterns(profile);
              const counts = relationCounts(profile);
              const complete = isProfileComplete(profile);
              const key = canonicalKey(profile);
              return (
                <tr key={profile.id} data-testid={`profile-row-${key || profile.id}`}>
                  <td>
                    <OrderedPatternTrail first={ordered.first} second={ordered.second} third={ordered.third} locale={locale} t={t} />
                  </td>
                  <td>{localizedName(ordered.first, locale) || '—'}</td>
                  <td>{localizedName(ordered.second, locale) || '—'}</td>
                  <td>{localizedName(ordered.third, locale) || '—'}</td>
                  <td><CanonicalKey value={key} /></td>
                  <td>
                    {hasPersonalSummary(profile)
                      ? <Badge tone="success">{t('results.hasSummary')}</Badge>
                      : <Badge>{t('results.noSummary')}</Badge>}
                  </td>
                  <td>{counts.key_strength}</td>
                  <td>{counts.professional_growth_challenge_opportunity}</td>
                  <td>{counts.suggested_career_path}</td>
                  <td>{counts.skill_worth_developing}</td>
                  <td>
                    <Badge tone={complete ? 'success' : 'warning'}>{t(completenessLabelKey(profile))}</Badge>
                    {missingContentKeys(profile).length > 0 ? (
                      <span className="sr-only">{missingContentKeys(profile).join(', ')}</span>
                    ) : null}
                  </td>
                  <td>{formatDateTime(profile.updated_at, locale)}</td>
                  <RowActionsCell
                    label={`${t('actions.rowActions')} ${key || `#${profile.id}`}`}
                    items={[detailsAction(paths.topThreeProfileDetail(profile.id, versionId), t)]}
                  />
                </tr>
              );
            })}
          </tbody>
        </ListTable>
      ) : null}
      <Pagination
        page={meta.current_page || filters.page}
        lastPage={meta.last_page}
        total={meta.total}
        onPageChange={(page) => setFilters((current) => ({ ...current, page }))}
        label={t('pagination.page')}
        ofLabel={t('pagination.of')}
        previousLabel={t('pagination.previous')}
        nextLabel={t('pagination.next')}
        dir={dir}
      />
    </div>
  );
}

export function TopThreeProfileDetailPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, locale, dir } = useI18n();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const canManage = hasPermission(PERMISSIONS.topThreeProfilesManage);
  const requestedVersionId = searchParams.get('version_id') || '';

  const versionsQuery = useQuery({
    queryKey: queryKeys.versions,
    queryFn: ({ signal }) => assessmentApi.listVersions({ signal }),
  });
  const versions = useMemo(() => versionsQuery.data?.items || [], [versionsQuery.data]);
  const selectedVersion = useMemo(
    () => (versionsQuery.isSuccess ? selectBuiltVersion(versions, requestedVersionId) : null),
    [requestedVersionId, versions, versionsQuery.isSuccess],
  );
  const versionId = resolveVersionPrimaryKey(selectedVersion)
    || (versionsQuery.isSuccess && isValidVersionId(requestedVersionId) ? String(requestedVersionId) : '');
  const versionReady = isValidVersionId(versionId) && versionsQuery.isSuccess;

  const detailQuery = useQuery({
    queryKey: queryKeys.topThreeProfile(id, versionId),
    enabled: Boolean(id && versionReady),
    queryFn: ({ signal }) => assessmentApi.getTopThreeProfile(versionId, id, { signal }),
  });

  useEffect(() => {
    if (!versionsQuery.isSuccess || !selectedVersion) {
      return;
    }
    const primaryKey = resolveVersionPrimaryKey(selectedVersion);
    if (!primaryKey || String(searchParams.get('version_id') || '') === primaryKey) {
      return;
    }
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('version_id', primaryKey);
      return next;
    }, { replace: true });
  }, [searchParams, selectedVersion, setSearchParams, versionsQuery.isSuccess]);

  useEffect(() => {
    if (detailQuery.error instanceof ApiError && detailQuery.error.isForbidden()) {
      navigate(paths.forbidden);
    }
  }, [detailQuery.error, navigate]);

  if (versionsQuery.isLoading) {
    return <Skeleton rows={6} />;
  }
  if (!versionReady) {
    return <EmptyState title={t('profiles.noBuiltVersion')} />;
  }
  if (detailQuery.isLoading) return <Skeleton rows={6} />;
  if (detailQuery.isError) return <ErrorState title={humanizeApiError(detailQuery.error, t)} onRetry={detailQuery.refetch} retryLabel={t('actions.retry')} />;

  const profile = detailQuery.data || {};
  const ordered = readOrderedPatterns(profile);
  const readOnly = isPublishedProfile(profile);
  const key = canonicalKey(profile);

  return (
    <div className="page-stack">
      <PageHeader
        title={t('profiles.detailTitle', { key: key || `#${profile.id}` })}
        backTo={paths.topThreeProfilesWithVersion(versionId)}
      />
      <p className="muted">{t('profiles.identityNote')}</p>
      <p>{t('results.version')}: {profile.version_id ?? profile.version?.id ?? versionId}</p>
      <OrderedPatternTrail first={ordered.first} second={ordered.second} third={ordered.third} locale={locale} t={t} />
      <p>
        <strong>{t('profiles.canonicalKey')}:</strong> <CanonicalKey value={key} />
      </p>
      {readOnly ? <p className="warning-banner" role="status">{t('profiles.publishedReadOnly')}</p> : null}
      <ProfileConnectionsEditor
        profileId={id}
        profile={profile}
        versionId={versionId || profile.version_id || profile.version?.id}
        canManage={canManage}
        readOnly={readOnly}
        t={t}
        locale={locale}
        dir={dir}
      />
    </div>
  );
}
